import { useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useToast } from '@orbind/ui'
import { useExplorer } from '../shell/explorer.tsx'
import {
  PRESENCE_LABEL,
  channelIcon,
  initialsOf,
  type ChatChannel,
  type ChatMessage,
} from '../mocks/chat.ts'
import './channels.css'

/**
 * Peças compartilhadas pelas conversas (Comunicação, Grupos e Canais de avisos).
 *
 * As tres telas desenham a mesma coisa: uma lista no explorer do shell e uma
 * conversa no `<main>`. O que muda e a fonte dos dados e o fluxo de criacao,
 * entao a linha da lista, o drawer de criacao e a conversa vivem aqui.
 */

/* ------------------------------------------------------------- drawer --- */

/**
 * Gaveta de altura total sobre o explorer — protótipo `_shell.html:46-87`.
 *
 * O protótipo NAO usa modal centralizado para criar canal/grupo: a gaveta cobre
 * o painel lateral inteiro. Como o explorer pertence ao shell (e o shell nao e
 * editavel por esta tela), a gaveta viaja por portal ate a `<section>` que
 * envolve o corpo rolavel — `channels.css` da a ela `position: relative`.
 */
export function ExplorerDrawer({
  title,
  subtitle,
  onClose,
  footer,
  children,
}: {
  title: string
  subtitle: string
  onClose: () => void
  /** Sem rodapé a gaveta não desenha a barra vazia (ex.: Nova conversa). */
  footer?: ReactNode
  children: ReactNode
}) {
  const { body } = useExplorer()
  const host = body?.parentElement ?? null

  useEffect(() => {
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', aoTeclar)
    return () => window.removeEventListener('keydown', aoTeclar)
  }, [onClose])

  // Explorer recolhido: nao ha onde ancorar a gaveta.
  if (host === null) return null

  return createPortal(
    <div className="ch-drawer" role="dialog" aria-modal="true" aria-label={title}>
      <div className="ch-drawer__head">
        <button type="button" className="ch-drawer__back" aria-label="Voltar" title="Voltar" onClick={onClose}>
          <i className="fas fa-arrow-left" aria-hidden />
        </button>
        <div className="ch-drawer__id">
          <span className="ch-drawer__title">{title}</span>
          <span className="ch-drawer__sub">{subtitle}</span>
        </div>
      </div>
      <div className="ch-drawer__body">{children}</div>
      {footer ? <div className="ch-drawer__foot">{footer}</div> : null}
    </div>,
    host,
  )
}

/* -------------------------------------------------- linha da listagem --- */

export function ChatRow({
  channel,
  selected,
  unread,
  onClick,
  trailing,
}: {
  channel: ChatChannel
  selected: boolean
  unread: number
  onClick: () => void
  /**
   * Acao propria da linha (ex.: "Seguir"). Fica FORA do `<button>` da linha —
   * botao dentro de botao nao e navegavel por teclado.
   */
  trailing?: ReactNode
}) {
  const linha = (
    <button type="button" className={`ch-row${selected ? ' ch-row--on' : ''}`} onClick={onClick}>
      <span className="ch-avatarwrap">
        <span className="ch-avatar">
          {channel.kind === 'pessoa' ? (
            initialsOf(channel.name)
          ) : (
            <i className={channelIcon(channel)} aria-hidden style={{ fontSize: 13 }} />
          )}
        </span>
        {channel.presence ? (
          <span
            className={`ch-dot ch-dot--${channel.presence}`}
            title={PRESENCE_LABEL[channel.presence]}
            aria-label={PRESENCE_LABEL[channel.presence]}
            role="img"
          />
        ) : null}
      </span>
      <span className="ch-row__main">
        <span className="ch-row__top">
          <span className="ch-row__title">{channel.name}</span>
          {channel.liveMeetingId ? (
            <span className="ch-row__live">
              <i className="fas fa-video" aria-hidden style={{ fontSize: 8 }} />
              Ao vivo
            </span>
          ) : null}
          <span className="ch-row__time">{channel.lastMessageAt}</span>
        </span>
        <span className="ch-row__bottom">
          <span className="ch-row__preview">{channel.lastMessagePreview}</span>
          {trailing === undefined && unread > 0 ? (
            <span className="ch-row__unread" aria-label={`${unread} mensagens não lidas`}>
              {unread}
            </span>
          ) : null}
        </span>
      </span>
    </button>
  )

  if (trailing === undefined) return linha
  return (
    <div className="ch-rowwrap">
      {linha}
      <span className="ch-rowwrap__action">{trailing}</span>
    </div>
  )
}

/* ---------------------------------------------- agrupamento por autor --- */

export type RenderedMessage = { message: ChatMessage; showAuthor: boolean }

/**
 * Esconde o nome do autor quando ele repete a mensagem anterior.
 *
 * Protótipo `_shell.html:2782-2792`: separador de data e mensagem de sistema
 * zeram o autor anterior, e mensagens minhas nunca mostram o nome.
 */
export function withAuthorGrouping(messages: readonly ChatMessage[]): RenderedMessage[] {
  const saida: RenderedMessage[] = []
  let autorAnterior: string | null = null
  for (const message of messages) {
    if (message.kind !== 'message') {
      saida.push({ message, showAuthor: false })
      autorAnterior = null
      continue
    }
    const showAuthor = !message.mine && message.author !== autorAnterior
    autorAnterior = message.author
    saida.push({ message, showAuthor })
  }
  return saida
}

/* ----------------------------------------------------------- conversa --- */

export type ChatConversationProps = {
  channel: ChatChannel
  messages: readonly ChatMessage[]
  /** Id da primeira nao lida quando a conversa abriu — ancora o separador. */
  dividerId?: string | null
  draft: string
  onDraftChange: (texto: string) => void
  onSend: () => void
  onOpenContext?: (code: string) => void
  onOpenMeeting?: () => void
  onCreateWorkItem?: () => void
  /** Sobrescreve a linha de participantes do cabecalho. */
  subtitle?: string
  /** Acao do botao "Silenciar" da barra somente leitura. */
  onMute?: () => void
  /** Conteudo extra na barra somente leitura (ex.: "Seguir canal"). */
  readOnlyExtra?: ReactNode
  /** Texto da barra somente leitura. */
  readOnlyLabel?: string
}

export function ChatConversation({
  channel,
  messages,
  dividerId = null,
  draft,
  onDraftChange,
  onSend,
  onOpenContext,
  onOpenMeeting,
  onCreateWorkItem,
  subtitle,
  onMute,
  readOnlyExtra,
  readOnlyLabel = 'Somente administradores do canal podem enviar mensagens',
}: ChatConversationProps) {
  const toast = useToast()
  const msgsRef = useRef<HTMLDivElement>(null)
  const composerRef = useRef<HTMLInputElement>(null)
  const [menuAberto, setMenuAberto] = useState(false)

  useEffect(() => {
    const lista = msgsRef.current
    if (lista) lista.scrollTop = lista.scrollHeight
  }, [channel.id, messages.length])

  useEffect(() => setMenuAberto(false), [channel.id])

  const emBreve = (mensagem: string) => toast.push({ tone: 'info', message: mensagem })

  const participantes =
    subtitle ??
    (channel.kind === 'pessoa'
      ? (channel.presence ? PRESENCE_LABEL[channel.presence] : 'Conversa direta')
      : `${channel.memberCount} participantes · ${channel.members.slice(0, 3).join(', ')}${
          channel.memberCount > 3 ? ' e outros' : ''
        }`)

  const renderizadas = withAuthorGrouping(messages)

  return (
    <div className="ch-conv">
      <div className="ch-conv__head">
        <span className="ch-avatarwrap">
          <span className="ch-avatar ch-avatar--lg">
            {channel.kind === 'pessoa' ? (
              initialsOf(channel.name)
            ) : (
              <i className={channelIcon(channel)} aria-hidden style={{ fontSize: 15 }} />
            )}
          </span>
          {channel.presence ? (
            <span
              className={`ch-dot ch-dot--${channel.presence}`}
              title={PRESENCE_LABEL[channel.presence]}
              aria-label={PRESENCE_LABEL[channel.presence]}
              role="img"
            />
          ) : null}
        </span>
        <div className="ch-conv__id">
          <span className="ch-conv__title">{channel.name}</span>
          <span className="ch-conv__sub">{participantes}</span>
        </div>

        {channel.contextCode ? (
          <button
            type="button"
            className="ch-ctxbtn"
            title="Abrir contexto vinculado"
            onClick={() => onOpenContext?.(channel.contextCode ?? '')}
          >
            <i className="fas fa-link" aria-hidden style={{ fontSize: 10 }} />
            {channel.contextCode}
          </button>
        ) : null}

        {channel.liveMeetingId ? (
          <button
            type="button"
            className="ch-livebtn"
            title="Voltar para a reunião deste canal"
            onClick={() => onOpenMeeting?.()}
          >
            <span className="ch-livebtn__dot" aria-hidden />
            Reunião em andamento · Entrar
          </button>
        ) : null}

        {/* par segmentado do protótipo (showChat.html:20-21) */}
        <div className="ch-meetgroup">
          <button
            type="button"
            className="ch-meetbtn"
            title="Iniciar reunião com vídeo agora"
            onClick={() => emBreve('Iniciar reunião ainda não está disponível nesta prévia.')}
          >
            <i className="fas fa-video" aria-hidden style={{ fontSize: 13 }} />
            Reunião
          </button>
          <button
            type="button"
            className="ch-schedbtn"
            aria-label="Agendar reunião"
            title="Agendar reunião interna"
            onClick={() => emBreve('Agendar reunião ainda não está disponível nesta prévia.')}
          >
            <i className="far fa-calendar-plus" aria-hidden style={{ fontSize: 13 }} />
          </button>
        </div>

        <button
          type="button"
          className="ch-iconbtn"
          aria-label="Buscar na conversa"
          title="Buscar na conversa"
          onClick={() => emBreve('Busca na conversa ainda não está disponível nesta prévia.')}
        >
          <i className="fas fa-search" aria-hidden style={{ fontSize: 14 }} />
        </button>

        {/* Mais opções — showChat.html:23 */}
        <div className="ch-more">
          <button
            type="button"
            className="ch-iconbtn"
            aria-label="Mais opções"
            aria-expanded={menuAberto}
            aria-haspopup="menu"
            title="Mais opções"
            onClick={() => setMenuAberto((aberto) => !aberto)}
          >
            <i className="fas fa-ellipsis-v" aria-hidden style={{ fontSize: 14 }} />
          </button>
          {menuAberto ? (
            <>
              <div className="ch-more__scrim" aria-hidden onClick={() => setMenuAberto(false)} />
              <div className="ch-more__menu" role="menu" aria-label={`Opções de ${channel.name}`}>
                <button
                  type="button"
                  role="menuitem"
                  className="ch-more__item"
                  onClick={() => {
                    setMenuAberto(false)
                    emBreve(`${channel.memberCount} participantes · ${channel.members.join(', ')}`)
                  }}
                >
                  <i className="fas fa-user-friends" aria-hidden />
                  Ver participantes
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="ch-more__item"
                  onClick={() => {
                    setMenuAberto(false)
                    if (onMute) onMute()
                    else emBreve(`“${channel.name}” silenciado nesta sessão.`)
                  }}
                >
                  <i className="fas fa-bell-slash" aria-hidden />
                  Silenciar
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="ch-more__item"
                  onClick={() => {
                    setMenuAberto(false)
                    emBreve('Exportar a conversa ainda não está disponível nesta prévia.')
                  }}
                >
                  <i className="fas fa-file-export" aria-hidden />
                  Exportar conversa
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>

      <div className="ch-msgs" ref={msgsRef}>
        {renderizadas.length === 0 ? (
          <div style={{ margin: 'auto' }}>
            <ChatEmpty channel={channel} onWrite={() => composerRef.current?.focus()} />
          </div>
        ) : (
          renderizadas.map(({ message, showAuthor }) => (
            <div key={message.id}>
              {dividerId === message.id ? (
                <div className="ch-newline">
                  <span>Não lidas</span>
                </div>
              ) : null}

              {message.kind === 'date' ? (
                <div className="ch-daysep">
                  <span>{message.text}</span>
                </div>
              ) : message.kind === 'system' ? (
                <div className="ch-sys">
                  <button
                    type="button"
                    className="ch-sys__pill"
                    onClick={() => (message.contextCode ? onOpenContext?.(message.contextCode) : undefined)}
                  >
                    <i className="fas fa-bolt" aria-hidden style={{ fontSize: 9 }} />
                    {message.text}
                  </button>
                </div>
              ) : (
                <div className={`ch-msgrow${message.mine ? ' ch-msgrow--mine' : ''}`}>
                  <div className={`ch-bubble${message.mine ? ' ch-bubble--mine' : ''}`}>
                    {showAuthor && channel.kind !== 'pessoa' ? (
                      <span className="ch-bubble__author">{message.author}</span>
                    ) : null}
                    <span className="ch-bubble__text">{message.text}</span>
                    <span className="ch-bubble__meta">
                      {message.time}
                      {message.mine ? (
                        <>
                          <i className="fas fa-check-double" aria-hidden style={{ fontSize: 9 }} />
                          <span className="ob-sr-only">Lida</span>
                        </>
                      ) : null}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {channel.readOnly ? (
        <div className="ch-readonly">
          <i className="fas fa-lock" aria-hidden style={{ fontSize: 11 }} />
          <span>{readOnlyLabel}</span>
          {readOnlyExtra}
          {/* Silenciar — showChat.html:59 */}
          <button
            type="button"
            className="ch-mutebtn"
            onClick={() => (onMute ? onMute() : emBreve(`“${channel.name}” silenciado nesta sessão.`))}
          >
            Silenciar
          </button>
        </div>
      ) : (
        <div className="ch-composer">
          <button
            type="button"
            className="ch-composer__tool"
            aria-label="Anexar arquivo"
            title="Anexar arquivo"
            onClick={() => emBreve('Anexos ainda não estão disponíveis nesta prévia.')}
          >
            <i className="fas fa-plus" aria-hidden style={{ fontSize: 16 }} />
          </button>
          <button
            type="button"
            className="ch-composer__tool"
            aria-label="Criar Item de Trabalho a partir da mensagem"
            title="Criar Item de Trabalho a partir da mensagem"
            onClick={() =>
              onCreateWorkItem
                ? onCreateWorkItem()
                : emBreve('Criar Item de Trabalho pela conversa ainda não está disponível nesta prévia.')
            }
          >
            <i className="fas fa-tasks" aria-hidden style={{ fontSize: 15 }} />
          </button>
          <input
            ref={composerRef}
            className="ch-composer__input"
            placeholder="Digite uma mensagem"
            aria-label={`Mensagem para ${channel.name}`}
            value={draft}
            onChange={(evento) => onDraftChange(evento.target.value)}
            onKeyDown={(evento) => {
              if (evento.key === 'Enter' && !evento.shiftKey) {
                evento.preventDefault()
                onSend()
              }
            }}
          />
          <button
            type="button"
            className="ch-composer__send"
            aria-label="Enviar mensagem"
            title="Enviar mensagem"
            disabled={draft.trim() === ''}
            onClick={onSend}
          >
            <i className="fas fa-paper-plane" aria-hidden style={{ fontSize: 14 }} />
          </button>
        </div>
      )}
    </div>
  )
}

/** Conversa sem mensagem — estado vazio proprio, com CTA para escrever. */
function ChatEmpty({ channel, onWrite }: { channel: ChatChannel; onWrite: () => void }) {
  return (
    <div className="ch-emptychat">
      <span className="ch-emptychat__mark">
        <i className="far fa-paper-plane" aria-hidden />
      </span>
      <span className="ch-emptychat__title">Nenhuma mensagem ainda</span>
      <span className="ch-emptychat__text">
        {channel.readOnly
          ? 'Este canal ainda não publicou nenhum aviso.'
          : `Comece a conversa com ${channel.name}.`}
      </span>
      {channel.readOnly ? null : (
        <button type="button" className="ch-emptychat__cta" onClick={onWrite}>
          <i className="fas fa-pen" aria-hidden style={{ fontSize: 11 }} />
          Enviar a primeira mensagem
        </button>
      )}
    </div>
  )
}
